import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import InterviewSchedulerModal from './InterviewSchedulerModal';
import CopyButton from '../common/CopyButton';
import { CandidateCardSkeleton } from '../common/Skeleton';

const BookedCandidatesList = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterJob, setFilterJob] = useState('all');
  const [filterRound, setFilterRound] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date-asc');
  const [copiedId, setCopiedId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [hideHired, setHideHired] = useState(false);

  // Multi-round scheduler modal states
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] = useState(null);
  const [schedulerJobOpeningId, setSchedulerJobOpeningId] = useState(null);
  const [initialRoundToSchedule, setInitialRoundToSchedule] = useState('L2_TECH');

  // Interactive evaluation scorecard states
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [evaluatingInterview, setEvaluatingInterview] = useState(null);
  const [evalForm, setEvalForm] = useState({
    communicationRating: 4,
    technicalRating: 4,
    overallRating: 4,
    notes: ''
  });
  const [submittingEval, setSubmittingEval] = useState(false);

  const fetchBookedCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/interviews/all-scheduled');
      setInterviews(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading booked candidates:', err);
      setError('Unable to load booked candidates. Please check your network or server connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookedCandidates();
  }, []);

  // Clear toast after 4s
  useEffect(() => {
    if (toastMessage) {
      const t = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toastMessage]);

  // Unique job titles for filter dropdown
  const jobOptions = useMemo(() => {
    const set = new Set();
    interviews.forEach(i => {
      if (i.job_title) set.add(i.job_title);
    });
    return Array.from(set);
  }, [interviews]);

  // KPI calculations
  const kpis = useMemo(() => {
    const total = interviews.length;
    const l1 = interviews.filter(i => i.round_type === 'L1_TECH').length;
    const l2 = interviews.filter(i => i.round_type === 'L2_TECH').length;
    const l3 = interviews.filter(i => i.round_type === 'L3_MGR' || i.round_type === 'HR_FIT').length;
    const hired = interviews.filter(i => i.candidate_status === 'HIRED').length;

    const todayStr = new Date().toISOString().split('T')[0];
    const today = interviews.filter(i => {
      if (!i.scheduled_date) return false;
      return i.scheduled_date.startsWith(todayStr);
    }).length;

    return { total, l1, l2, l3, hired, today };
  }, [interviews]);

  // Filter and sort candidates
  const filteredInterviews = useMemo(() => {
    return interviews
      .filter(item => {
        // Hide Hired filter toggle
        if (hideHired && item.candidate_status === 'HIRED') {
          return false;
        }

        // Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchName = item.candidate_name?.toLowerCase().includes(q);
          const matchEmail = item.candidate_email?.toLowerCase().includes(q);
          const matchJob = item.job_title?.toLowerCase().includes(q);
          const matchInterviewer = item.interviewer_name?.toLowerCase().includes(q);
          const matchRoom = item.room_id?.toLowerCase().includes(q);
          if (!matchName && !matchEmail && !matchJob && !matchInterviewer && !matchRoom) {
            return false;
          }
        }

        // Job filter
        if (filterJob !== 'all' && item.job_title !== filterJob) {
          return false;
        }

        // Round filter
        if (filterRound !== 'all' && item.round_type !== filterRound) {
          return false;
        }

        // Status filter
        if (filterStatus !== 'all') {
          if (filterStatus === 'HIRED') {
            if (item.candidate_status !== 'HIRED') return false;
          } else if (filterStatus === 'COMPLETED') {
            if (item.status !== 'COMPLETED') return false;
          } else if (filterStatus === 'SCHEDULED') {
            if (item.status !== 'SCHEDULED') return false;
          } else if (filterStatus === 'CANCELLED') {
            if (item.status !== 'CANCELLED') return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'date-asc') {
          return new Date(a.scheduled_date) - new Date(b.scheduled_date);
        }
        if (sortBy === 'date-desc') {
          return new Date(b.scheduled_date) - new Date(a.scheduled_date);
        }
        if (sortBy === 'score-desc') {
          return (b.matching_percentage || 0) - (a.matching_percentage || 0);
        }
        if (sortBy === 'name-asc') {
          return (a.candidate_name || '').localeCompare(b.candidate_name || '');
        }
        return 0;
      });
  }, [interviews, searchQuery, filterJob, filterRound, filterStatus, hideHired, sortBy]);

  // Copy candidate link or room ID
  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(key);
    setTimeout(() => setCopiedId(null), 2500);
    setToastMessage({ type: 'success', text: `Copied to clipboard: ${text}` });
  };

  // Resend email invitation
  const handleResendInvite = async (scheduleId, candidateName) => {
    try {
      setResendingId(scheduleId);
      const res = await api.post('/api/interviews/resend-invite', { scheduleId });
      setToastMessage({
        type: 'success',
        text: res.data?.message || `Invitation resent successfully to ${candidateName}!`
      });
    } catch (err) {
      console.error('Error resending invite:', err);
      setToastMessage({
        type: 'error',
        text: 'Failed to resend invitation. Please try again.'
      });
    } finally {
      setResendingId(null);
    }
  };

  // Helper to determine subsequent technical/management round
  const getNextRound = (currentRound) => {
    if (currentRound === 'L1_TECH') return 'L2_TECH';
    if (currentRound === 'L2_TECH') return 'L3_MGR';
    if (currentRound === 'L3_MGR') return 'HR_FIT';
    return 'HR_FIT';
  };

  // Promote cleared candidate directly to Hired & Onboarding stage
  const handlePromoteToHired = async (candidateId, candidateName) => {
    try {
      await api.put(`/api/screening/candidate/${candidateId}/status`, { status: 'HIRED' });
      setToastMessage({
        type: 'success',
        text: `🎉 Congratulations! ${candidateName} has been cleared and marked as HIRED. Ready for Offer Letter & Onboarding!`
      });
      fetchBookedCandidates();
    } catch (err) {
      console.error('Error promoting candidate:', err);
      setToastMessage({ type: 'error', text: 'Failed to update candidate status to HIRED.' });
    }
  };

  // Submit interviewer evaluation scorecard
  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    if (!evaluatingInterview) return;
    try {
      setSubmittingEval(true);
      const res = await api.post('/api/interviews/evaluate', {
        interviewId: evaluatingInterview.id,
        ...evalForm
      });
      setToastMessage({
        type: 'success',
        text: res.data?.message || `Evaluation saved successfully! Candidate marked as cleared.`
      });
      setShowEvalModal(false);
      fetchBookedCandidates();
    } catch (err) {
      console.error('Error saving evaluation:', err);
      setToastMessage({ type: 'error', text: 'Failed to save evaluation.' });
    } finally {
      setSubmittingEval(false);
    }
  };

  // Formatting helpers
  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: 'Not set', time: '' };
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getRoundBadge = (roundType) => {
    switch (roundType) {
      case 'L1_TECH':
        return { label: '💻 L1 Technical', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
      case 'L2_TECH':
        return { label: '🏗️ L2 Architecture', bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' };
      case 'L3_MGR':
        return { label: '👥 L3 Managerial / Fit', bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
      case 'HR_FIT':
        return { label: '🤝 HR Discussion', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' };
      default:
        return { label: roundType?.replace('_', ' ') || 'Interview', bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
    }
  };

  const getStatusBadge = (status, candidateStatus) => {
    if (candidateStatus === 'HIRED') {
      return { label: '🎉 Hired & Offer Sent', bg: '#f5f3ff', color: '#6d28d9', dot: '#8b5cf6', isHired: true };
    }
    switch (status) {
      case 'SCHEDULED':
        return { label: 'Scheduled', bg: '#ecfdf5', color: '#065f46', dot: '#10b981' };
      case 'COMPLETED':
        return { label: 'Completed', bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' };
      case 'CANCELLED':
        return { label: 'Cancelled', bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' };
      default:
        return { label: status || 'Pending', bg: '#f8fafc', color: '#475569', dot: '#94a3b8' };
    }
  };

  return (
    <div style={styles.container}>
      {/* HEADER SECTION */}
      <div style={styles.header}>
        <div style={styles.headerTitleGroup}>
          <div style={styles.iconCircle}>
            <svg style={styles.headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <h2 style={styles.title}>Booked Candidates Roster</h2>
            <p style={styles.subtitle}>
              Monitor all candidates with confirmed interview slots, assigned interviewers, room access, and scheduling links.
            </p>
          </div>
        </div>

        <div style={styles.headerActions}>
          <button
            onClick={fetchBookedCandidates}
            disabled={loading}
            style={styles.refreshBtn}
            title="Refresh list"
          >
            <svg
              style={{ ...styles.actionIcon, animation: loading ? 'spin 1s linear infinite' : 'none' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>

          <button
            onClick={() => navigate('/hr-dashboard/screening')}
            style={styles.scheduleMoreBtn}
          >
            <svg style={styles.actionIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Schedule More Candidates</span>
          </button>
        </div>
      </div>

      {/* TOAST ALERT */}
      {toastMessage && (
        <div
          style={{
            ...styles.toast,
            backgroundColor: toastMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
            borderColor: toastMessage.type === 'success' ? '#a7f3d0' : '#fecaca',
            color: toastMessage.type === 'success' ? '#065f46' : '#991b1b'
          }}
        >
          <span>{toastMessage.type === 'success' ? '✓' : '⚠️'}</span>
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* KPI METRIC CARDS */}
      <div style={styles.kpiGrid}>
        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #2563eb' }}>
          <div style={styles.kpiNumber}>{kpis.total}</div>
          <div style={styles.kpiLabel}>Total Booked Candidates</div>
          <div style={styles.kpiSub}>Confirmed interview slots</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #10b981' }}>
          <div style={styles.kpiNumber}>{kpis.today}</div>
          <div style={styles.kpiLabel}>Interviews Today</div>
          <div style={styles.kpiSub}>Scheduled for current date</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #3b82f6' }}>
          <div style={styles.kpiNumber}>{kpis.l1}</div>
          <div style={styles.kpiLabel}>L1 Technical Rounds</div>
          <div style={styles.kpiSub}>Coding & system fundamentals</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #8b5cf6' }}>
          <div style={styles.kpiNumber}>{kpis.l2}</div>
          <div style={styles.kpiLabel}>L2 Architecture Rounds</div>
          <div style={styles.kpiSub}>System design & depth</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #f59e0b' }}>
          <div style={styles.kpiNumber}>{kpis.l3}</div>
          <div style={styles.kpiLabel}>L3 / HR Rounds</div>
          <div style={styles.kpiSub}>Leadership & culture fit</div>
        </div>

        <div style={{ ...styles.kpiCard, borderLeft: '4px solid #7c3aed' }}>
          <div style={styles.kpiNumber}>{kpis.hired}</div>
          <div style={styles.kpiLabel}>Candidates Hired 🎉</div>
          <div style={styles.kpiSub}>Cleared & offer sent</div>
        </div>
      </div>

      {/* SEARCH AND FILTER BAR */}
      <div style={styles.filterToolbar}>
        {/* Search Input */}
        <div style={styles.searchWrapper}>
          <svg style={styles.searchIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search candidate, email, interviewer, room ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.clearSearchBtn}>✕</button>
          )}
        </div>

        {/* Filter Controls */}
        <div style={styles.filterControls}>
          {/* Job Position Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Position</label>
            <select
              value={filterJob}
              onChange={(e) => setFilterJob(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Positions ({jobOptions.length})</option>
              {jobOptions.map(job => (
                <option key={job} value={job}>{job}</option>
              ))}
            </select>
          </div>

          {/* Round Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Round Level</label>
            <select
              value={filterRound}
              onChange={(e) => setFilterRound(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Rounds</option>
              <option value="L1_TECH">💻 L1 Technical</option>
              <option value="L2_TECH">🏗️ L2 Architecture</option>
              <option value="L3_MGR">👥 L3 Managerial</option>
              <option value="HR_FIT">🤝 HR Discussion</option>
            </select>
          </div>

          {/* Status Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Statuses ({interviews.length})</option>
              <option value="SCHEDULED">📅 Scheduled / Upcoming</option>
              <option value="COMPLETED">✅ Completed</option>
              <option value="HIRED">🎉 Hired & Onboarded ({kpis.hired})</option>
              <option value="CANCELLED">❌ Cancelled</option>
            </select>
          </div>

          {/* Sort By */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.select}
            >
              <option value="date-asc">Date (Earliest First)</option>
              <option value="date-desc">Date (Furthest First)</option>
              <option value="score-desc">Match Score (High to Low)</option>
              <option value="name-asc">Candidate Name (A-Z)</option>
            </select>
          </div>

          {/* Hide Hired Toggle */}
          <div style={styles.toggleGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hideHired}
                onChange={(e) => setHideHired(e.target.checked)}
                style={styles.checkboxInput}
              />
              <span>Hide Hired ({kpis.hired})</span>
            </label>
          </div>
        </div>
      </div>

      {/* AUDIT NOTICE BANNER */}
      <div style={styles.auditInfoBanner}>
        <div style={styles.auditInfoIcon}>ℹ️</div>
        <div style={styles.auditInfoText}>
          <strong>Interview Roster & Audit History:</strong> This page preserves the complete audit history of all scheduled interview sessions, interviewer scorecards, and room links. Candidates marked as <strong>Hired</strong> remain in this roster for historical record keeping, and are concurrently active in the{' '}
          <button onClick={() => navigate('/hr-dashboard/pipeline')} style={styles.bannerLinkBtn}>
            Candidate Pipeline ➔
          </button>
        </div>
      </div>

      {/* RESULTS COUNT */}
      <div style={styles.resultsMeta}>
        <span>
          Showing <strong>{filteredInterviews.length}</strong> of <strong>{interviews.length}</strong> booked candidates
        </span>
        {(searchQuery || filterJob !== 'all' || filterRound !== 'all' || filterStatus !== 'all') && (
          <button
            onClick={() => {
              setSearchQuery('');
              setFilterJob('all');
              setFilterRound('all');
              setFilterStatus('all');
            }}
            style={styles.resetFiltersBtn}
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* MAIN CONTENT / CANDIDATE LIST */}
      {loading ? (
        <CandidateCardSkeleton count={3} />
      ) : error ? (
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>⚠️ {error}</p>
          <button onClick={fetchBookedCandidates} style={styles.retryBtn}>Retry</button>
        </div>
      ) : filteredInterviews.length === 0 ? (
        <div style={styles.emptyContainer}>
          <div style={styles.emptyIcon}>📅</div>
          <h3 style={styles.emptyTitle}>
            {interviews.length === 0 ? 'No Booked Interviews Yet' : 'No Matching Candidates'}
          </h3>
          <p style={styles.emptySubtitle}>
            {interviews.length === 0
              ? 'Shortlisted candidates from the AI Screening module will appear here once an interview slot is confirmed.'
              : 'Try changing your search terms or relaxing the filters to view other scheduled interviews.'}
          </p>
          {interviews.length === 0 && (
            <button
              onClick={() => navigate('/hr-dashboard/screening')}
              style={styles.emptyActionBtn}
            >
              Go to Candidate AI Screening →
            </button>
          )}
        </div>
      ) : (
        <div style={styles.cardList}>
          {filteredInterviews.map((item) => {
            const { date, time } = formatDateTime(item.scheduled_date);
            const roundBadge = getRoundBadge(item.round_type);
            const statusBadge = getStatusBadge(item.status, item.candidate_status);
            const isExpanded = expandedId === item.id;
            const isHired = item.candidate_status === 'HIRED';
            const initials = (item.candidate_name || 'C')
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);

            return (
              <div
                key={item.id}
                style={{
                  ...styles.card,
                  borderColor: isHired ? '#c4b5fd' : 'var(--border-color, #e2e8f0)',
                  backgroundColor: isHired ? '#faf5ff' : 'var(--bg-modal-card, #ffffff)'
                }}
              >
                <div style={styles.cardMain}>
                  {/* LEFT: CANDIDATE INFO */}
                  <div style={styles.candidateCol}>
                    <div style={styles.avatarWrapper}>
                      <div
                        style={{
                          ...styles.avatar,
                          backgroundColor: isHired ? '#7c3aed' : '#4338ca',
                          boxShadow: isHired ? '0 0 0 3px #ddd6fe' : 'none'
                        }}
                      >
                        {initials}
                      </div>
                    </div>
                    <div style={styles.candidateDetails}>
                      <div style={styles.candidateNameRow}>
                        <h4 style={styles.candidateName}>{item.candidate_name}</h4>
                        {isHired && (
                          <span style={styles.hiredNameBadge}>
                            🎉 Hired & Offer Sent
                          </span>
                        )}
                        {item.matching_percentage !== null && item.matching_percentage !== undefined && (
                          <span
                            style={{
                              ...styles.matchPill,
                              backgroundColor: item.matching_percentage >= 80 ? '#ecfdf5' : '#fffbeb',
                              color: item.matching_percentage >= 80 ? '#059669' : '#d97706',
                              borderColor: item.matching_percentage >= 80 ? '#a7f3d0' : '#fde68a'
                            }}
                          >
                            ⭐ {item.matching_percentage}% Match
                          </span>
                        )}
                      </div>

                      <div style={styles.contactRow}>
                        {item.candidate_email && (
                          <a href={`mailto:${item.candidate_email}`} style={styles.contactItem}>
                            <svg style={styles.contactIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>{item.candidate_email}</span>
                          </a>
                        )}
                        {item.candidate_phone && (
                          <span style={styles.contactItem}>
                            <svg style={styles.contactIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{item.candidate_phone}</span>
                          </span>
                        )}
                      </div>

                      <div style={styles.jobRow}>
                        <span style={styles.jobTag}>
                          💼 {item.job_title || 'Position'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* MIDDLE: INTERVIEW DETAILS */}
                  <div style={styles.scheduleCol}>
                    <div style={styles.scheduleMetaBox}>
                      <div style={styles.dateBlock}>
                        <div style={styles.dateText}>{date}</div>
                        <div style={styles.timeText}>{time}</div>
                      </div>

                      <div style={styles.badgesRow}>
                        <span
                          style={{
                            ...styles.roundBadge,
                            backgroundColor: roundBadge.bg,
                            color: roundBadge.color,
                            borderColor: roundBadge.border
                          }}
                        >
                          {roundBadge.label}
                        </span>

                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color
                          }}
                        >
                          <span style={{ ...styles.statusDot, backgroundColor: statusBadge.dot }} />
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>

                    {/* Interviewer Info */}
                    <div style={styles.interviewerInfo}>
                      <span style={styles.infoLabel}>Interviewer:</span>
                      <span style={styles.interviewerName}>{item.interviewer_name || 'Assigned Interviewer'}</span>
                      {item.interviewer_email && (
                        <span style={styles.interviewerEmail}>({item.interviewer_email})</span>
                      )}
                    </div>

                    {/* Room ID with copy */}
                    <div style={styles.roomRow}>
                      <span style={styles.infoLabel}>Room ID:</span>
                      <code style={styles.roomCode}>{item.room_id}</code>
                      <CopyButton 
                        text={item.room_id} 
                        variant="icon" 
                        iconSize={13} 
                        title="Copy Room ID to clipboard" 
                      />
                    </div>

                    {/* INTERVIEWER EVALUATION SCORECARD */}
                    {item.overall_rating ? (
                      <div style={styles.evaluationScoreBox}>
                        <div style={styles.evalScoreHeader}>
                          <span style={styles.evalScoreTitle}>⭐ Interviewer Scorecard:</span>
                          <span style={styles.evalScoreRating}>{item.overall_rating} / 5</span>
                        </div>
                        <div style={styles.evalScoreDetails}>
                          <span>Technical: <strong>{item.technical_rating || '-'}/5</strong></span>
                          <span>•</span>
                          <span>Communication: <strong>{item.communication_rating || '-'}/5</strong></span>
                        </div>
                        {item.interviewer_notes && (
                          <div style={styles.evalNotesSnippet}>
                            "{item.interviewer_notes}"
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {/* RIGHT: ACTION BUTTONS */}
                  <div style={styles.actionsCol}>
                    {/* Copy Candidate Link */}
                    {item.candidate_link && (
                      <CopyButton
                        text={item.candidate_link}
                        label="Candidate Link"
                        variant="button"
                        iconSize={14}
                        title="Copy candidate join URL to clipboard"
                      />
                    )}

                    {/* Join / Lobby Button */}
                    <a
                      href={item.candidate_link || `/lobby?roomId=${item.room_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.actionBtnPrimary}
                    >
                      <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Join / Enter Lobby</span>
                    </a>

                    {/* Record / Submit Evaluation (if scheduled and pending evaluation) */}
                    {item.status === 'SCHEDULED' && !item.overall_rating && (
                      <button
                        onClick={() => {
                          setEvaluatingInterview(item);
                          setEvalForm({
                            communicationRating: 4,
                            technicalRating: 4,
                            overallRating: 4,
                            notes: ''
                          });
                          setShowEvalModal(true);
                        }}
                        style={styles.recordEvalBtn}
                        title="Record evaluation scores and interviewer notes"
                      >
                        <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>Record Evaluation</span>
                      </button>
                    )}

                    {/* Schedule Next Round (when evaluated / cleared and not yet hired) */}
                    {(item.status === 'COMPLETED' || item.overall_rating) && item.round_type !== 'HR_FIT' && !isHired && (
                      <button
                        onClick={() => {
                          setCandidateToSchedule({
                            id: item.candidate_id,
                            name: item.candidate_name,
                            email: item.candidate_email
                          });
                          setSchedulerJobOpeningId(item.job_opening_id);
                          setInitialRoundToSchedule(getNextRound(item.round_type));
                          setShowSchedulerModal(true);
                        }}
                        style={styles.scheduleNextBtn}
                        title="Schedule next technical or managerial round"
                      >
                        <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Schedule {getNextRound(item.round_type).replace('_', ' ')}</span>
                      </button>
                    )}

                    {/* Candidate Cleared & Hired */}
                    {isHired ? (
                      <div style={styles.hiredActionBlock}>
                        <div style={styles.hiredActionBadge}>
                          <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Candidate Hired 🎉</span>
                        </div>
                        <button
                          onClick={() => navigate('/hr-dashboard/pipeline')}
                          style={styles.viewPipelineBtn}
                          title="View candidate in Candidate Pipeline"
                        >
                          <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                          <span>View in Pipeline</span>
                        </button>
                      </div>
                    ) : (
                      /* Advance to Hired & Onboarding */
                      (item.status === 'COMPLETED' || item.overall_rating) && (
                        <button
                          onClick={() => handlePromoteToHired(item.candidate_id, item.candidate_name)}
                          style={styles.hireBtn}
                          title="Candidate cleared all rounds: advance to Hired stage"
                        >
                          <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Mark Hired & Onboard</span>
                        </button>
                      )
                    )}

                    {/* Resend Invite Button */}
                    <button
                      onClick={() => handleResendInvite(item.id, item.candidate_name)}
                      disabled={resendingId === item.id}
                      style={styles.resendBtn}
                      title="Resend email with join links to candidate and interviewer"
                    >
                      <svg
                        style={{
                          ...styles.btnIcon,
                          animation: resendingId === item.id ? 'spin 1s linear infinite' : 'none'
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{resendingId === item.id ? 'Sending...' : 'Resend Invite'}</span>
                    </button>

                    {/* Toggle AI Insights */}
                    {(item.skills_extracted || item.strengths || item.gaps) && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : item.id)}
                        style={styles.toggleInsightsBtn}
                      >
                        <span>{isExpanded ? 'Hide AI Summary ▲' : 'AI Screening Summary ▼'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* COLLAPSIBLE AI SCREENING INSIGHTS */}
                {isExpanded && (
                  <div style={styles.expandedInsights}>
                    <div style={styles.insightsTitle}>
                      <span>🧠 AI Resume Screening Insights</span>
                    </div>

                    {item.skills_extracted && (
                      <div style={styles.insightSection}>
                        <span style={styles.insightLabel}>Extracted Skills:</span>
                        <div style={styles.skillsPills}>
                          {item.skills_extracted.split(',').map((skill, idx) => (
                            <span key={idx} style={styles.skillPill}>
                              {skill.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={styles.feedbackGrid}>
                      {item.strengths && (
                        <div style={styles.feedbackBlock}>
                          <span style={{ ...styles.feedbackLabel, color: '#059669' }}>✓ Key Strengths</span>
                          <p style={styles.feedbackText}>{item.strengths}</p>
                        </div>
                      )}

                      {item.gaps && (
                        <div style={styles.feedbackBlock}>
                          <span style={{ ...styles.feedbackLabel, color: '#dc2626' }}>⚠️ Gaps & Inquiries</span>
                          <p style={styles.feedbackText}>{item.gaps}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MULTI-ROUND INTERVIEW SCHEDULER MODAL */}
      {showSchedulerModal && (
        <InterviewSchedulerModal
          candidate={candidateToSchedule}
          jobOpeningId={schedulerJobOpeningId}
          initialRound={initialRoundToSchedule}
          onClose={() => setShowSchedulerModal(false)}
          onScheduleSuccess={() => {
            setShowSchedulerModal(false);
            setToastMessage({
              type: 'success',
              text: `Next round (${initialRoundToSchedule.replace('_', ' ')}) successfully scheduled for ${candidateToSchedule?.name}!`
            });
            fetchBookedCandidates();
          }}
        />
      )}

      {/* QUICK EVALUATION SCORECARD MODAL */}
      {showEvalModal && evaluatingInterview && (
        <div style={styles.modalBackdrop} onClick={() => setShowEvalModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalHeaderIcon}>📝</div>
                <div>
                  <h3 style={styles.modalTitle}>Record Interviewer Evaluation</h3>
                  <p style={styles.modalSubtitle}>
                    Candidate: <strong>{evaluatingInterview.candidate_name}</strong> • Round: <strong>{evaluatingInterview.round_type.replace('_', ' ')}</strong>
                  </p>
                </div>
              </div>
              <button onClick={() => setShowEvalModal(false)} style={styles.modalCloseBtn}>✕</button>
            </div>

            <form onSubmit={handleSubmitEvaluation} style={styles.evalForm}>
              <div style={styles.evalRatingsGrid}>
                <div style={styles.evalField}>
                  <label style={styles.evalLabel}>Technical Competency (1 - 5):</label>
                  <select
                    value={evalForm.technicalRating}
                    onChange={(e) => setEvalForm({ ...evalForm, technicalRating: Number(e.target.value) })}
                    style={styles.evalSelect}
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ 5 - Exceptional / Advanced</option>
                    <option value={4}>⭐⭐⭐⭐ 4 - Strong / Exceeds Requirements</option>
                    <option value={3}>⭐⭐⭐ 3 - Meets Requirements</option>
                    <option value={2}>⭐⭐ 2 - Marginal / Has Knowledge Gaps</option>
                    <option value={1}>⭐ 1 - Unsatisfactory</option>
                  </select>
                </div>

                <div style={styles.evalField}>
                  <label style={styles.evalLabel}>Communication & Culture Fit (1 - 5):</label>
                  <select
                    value={evalForm.communicationRating}
                    onChange={(e) => setEvalForm({ ...evalForm, communicationRating: Number(e.target.value) })}
                    style={styles.evalSelect}
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ 5 - Articulate & Collaborative</option>
                    <option value={4}>⭐⭐⭐⭐ 4 - Clear Communication</option>
                    <option value={3}>⭐⭐⭐ 3 - Adequate Communication</option>
                    <option value={2}>⭐⭐ 2 - Hesitant / Needs Improvement</option>
                    <option value={1}>⭐ 1 - Poor Communication</option>
                  </select>
                </div>

                <div style={styles.evalField}>
                  <label style={styles.evalLabel}>Overall Recommendation Score (1 - 5):</label>
                  <select
                    value={evalForm.overallRating}
                    onChange={(e) => setEvalForm({ ...evalForm, overallRating: Number(e.target.value) })}
                    style={styles.evalSelect}
                  >
                    <option value={5}>⭐⭐⭐⭐⭐ 5 - Strong Hire (Top 5%)</option>
                    <option value={4}>⭐⭐⭐⭐ 4 - Hire / Advance to Next Round</option>
                    <option value={3}>⭐⭐⭐ 3 - Marginal / Re-evaluate</option>
                    <option value={2}>⭐⭐ 2 - Do Not Advance</option>
                    <option value={1}>⭐ 1 - Strong Reject</option>
                  </select>
                </div>
              </div>

              <div style={styles.evalField}>
                <label style={styles.evalLabel}>Interviewer Assessment & Feedback Notes:</label>
                <textarea
                  rows={4}
                  placeholder="Summarize candidate's performance, code quality, edge cases handled, architecture design reasoning..."
                  value={evalForm.notes}
                  onChange={(e) => setEvalForm({ ...evalForm, notes: e.target.value })}
                  style={styles.evalTextarea}
                  required
                />
              </div>

              <div style={styles.evalModalActions}>
                <button
                  type="button"
                  onClick={() => setShowEvalModal(false)}
                  style={styles.evalCancelBtn}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingEval}
                  style={styles.evalSubmitBtn}
                >
                  {submittingEval ? 'Saving Scorecard...' : '✓ Save Evaluation & Clear Round'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    textAlign: 'left'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px'
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  headerIcon: {
    width: '24px',
    height: '24px'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800',
    letterSpacing: '-0.4px',
    color: 'var(--text-main, #0f172a)'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '13.5px',
    color: 'var(--text-muted, #64748b)'
  },
  headerActions: {
    display: 'flex',
    gap: '10px'
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  scheduleMoreBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  actionIcon: {
    width: '16px',
    height: '16px'
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid transparent',
    fontSize: '13px',
    fontWeight: '600'
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '14px'
  },
  kpiCard: {
    backgroundColor: 'var(--bg-modal-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '16px 18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  kpiNumber: {
    fontSize: '26px',
    fontWeight: '800',
    color: 'var(--text-main, #0f172a)'
  },
  kpiLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: 'var(--text-secondary, #334155)',
    marginTop: '2px'
  },
  kpiSub: {
    fontSize: '11.5px',
    color: 'var(--text-muted, #64748b)',
    marginTop: '2px'
  },
  filterToolbar: {
    backgroundColor: 'var(--bg-modal-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '14px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    width: '18px',
    height: '18px',
    color: '#94a3b8'
  },
  searchInput: {
    width: '100%',
    padding: '9px 36px 9px 38px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '13.5px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  clearSearchBtn: {
    position: 'absolute',
    right: '12px',
    border: 'none',
    background: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '14px'
  },
  filterControls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    alignItems: 'center'
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '150px',
    flex: '1 1 auto'
  },
  filterLabel: {
    fontSize: '11.5px',
    fontWeight: '700',
    color: 'var(--text-muted, #64748b)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  select: {
    padding: '7px 10px',
    borderRadius: '7px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    outline: 'none'
  },
  toggleGroup: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '2px',
    flex: '0 0 auto'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#475569',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: '#f8fafc',
    padding: '7px 12px',
    borderRadius: '7px',
    border: '1px solid #cbd5e1'
  },
  checkboxInput: {
    cursor: 'pointer',
    accentColor: '#7c3aed'
  },
  auditInfoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    borderRadius: '10px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    fontSize: '12.5px',
    lineHeight: '1.4'
  },
  auditInfoIcon: {
    fontSize: '18px',
    flexShrink: 0
  },
  auditInfoText: {
    flex: 1
  },
  bannerLinkBtn: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontWeight: '700',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontSize: '12.5px'
  },
  resultsMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)',
    padding: '0 4px'
  },
  resetFiltersBtn: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: '12.5px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  loadingContainer: {
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  loadingText: {
    fontSize: '13.5px',
    color: '#64748b'
  },
  errorContainer: {
    padding: '36px',
    backgroundColor: '#fef2f2',
    borderRadius: '12px',
    border: '1px solid #fecaca',
    textAlign: 'center'
  },
  errorText: {
    color: '#991b1b',
    fontWeight: '600',
    margin: '0 0 12px 0'
  },
  retryBtn: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    fontWeight: '600',
    cursor: 'pointer'
  },
  emptyContainer: {
    padding: '48px 24px',
    backgroundColor: 'var(--bg-modal-card, #ffffff)',
    borderRadius: '14px',
    border: '1px dashed #cbd5e1',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  },
  emptyIcon: {
    fontSize: '36px',
    marginBottom: '4px'
  },
  emptyTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  emptySubtitle: {
    margin: 0,
    fontSize: '13.5px',
    color: 'var(--text-muted, #64748b)',
    maxWidth: '460px'
  },
  emptyActionBtn: {
    marginTop: '10px',
    padding: '9px 18px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  card: {
    backgroundColor: 'var(--bg-modal-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '18px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    transition: 'all 0.15s ease'
  },
  cardMain: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
  },
  candidateCol: {
    display: 'flex',
    gap: '14px',
    flex: '1 1 300px',
    minWidth: '260px'
  },
  avatarWrapper: {
    flexShrink: 0
  },
  avatar: {
    width: '46px',
    height: '46px',
    borderRadius: '10px',
    backgroundColor: '#4338ca',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '800',
    fontSize: '15px'
  },
  candidateDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  candidateNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  candidateName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  matchPill: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 7px',
    borderRadius: '6px',
    border: '1px solid'
  },
  contactRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    fontSize: '12.5px',
    color: 'var(--text-muted, #64748b)'
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: 'inherit',
    textDecoration: 'none'
  },
  contactIcon: {
    width: '13px',
    height: '13px'
  },
  jobRow: {
    marginTop: '4px'
  },
  jobTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '6px',
    backgroundColor: '#f1f5f9',
    color: '#334155',
    fontSize: '12px',
    fontWeight: '600'
  },
  scheduleCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1 1 280px',
    minWidth: '240px'
  },
  scheduleMetaBox: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  dateBlock: {
    display: 'flex',
    flexDirection: 'column'
  },
  dateText: {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  timeText: {
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#2563eb'
  },
  badgesRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center'
  },
  roundBadge: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '3px 8px',
    borderRadius: '6px',
    border: '1px solid'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '11px',
    fontWeight: '700',
    padding: '3px 8px',
    borderRadius: '6px'
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%'
  },
  interviewerInfo: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px'
  },
  infoLabel: {
    fontWeight: '600',
    color: '#475569'
  },
  interviewerName: {
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  interviewerEmail: {
    color: '#94a3b8'
  },
  roomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px'
  },
  roomCode: {
    fontFamily: 'monospace',
    fontWeight: '700',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#0f172a'
  },
  copyTinyBtn: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: '11.5px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '2px 4px'
  },
  actionsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: '0 0 190px',
    minWidth: '170px'
  },
  actionBtnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '12.5px',
    fontWeight: '600',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s'
  },
  actionBtnSecondary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#334155',
    fontSize: '12.5px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  resendBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '7px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  toggleInsightsBtn: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    fontSize: '11.5px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '4px 0',
    textAlign: 'center'
  },
  btnIcon: {
    width: '14px',
    height: '14px'
  },
  expandedInsights: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: '1px dashed #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  insightsTitle: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#4338ca'
  },
  insightSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  insightLabel: {
    fontSize: '11.5px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase'
  },
  skillsPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  skillPill: {
    fontSize: '11.5px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#f1f5f9',
    color: '#1e293b'
  },
  feedbackGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '12px'
  },
  feedbackBlock: {
    backgroundColor: '#f8fafc',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #f1f5f9'
  },
  feedbackLabel: {
    fontSize: '11.5px',
    fontWeight: '700',
    display: 'block',
    marginBottom: '4px'
  },
  feedbackText: {
    margin: 0,
    fontSize: '12px',
    color: '#334155',
    lineHeight: '1.4'
  },
  evaluationScoreBox: {
    marginTop: '4px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: '#ecfdf5',
    border: '1px solid #a7f3d0'
  },
  evalScoreHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#065f46'
  },
  evalScoreTitle: {
    color: '#065f46'
  },
  evalScoreRating: {
    backgroundColor: '#059669',
    color: '#ffffff',
    padding: '2px 7px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '800'
  },
  evalScoreDetails: {
    display: 'flex',
    gap: '8px',
    fontSize: '11.5px',
    color: '#047857',
    marginTop: '4px',
    fontWeight: '600'
  },
  evalNotesSnippet: {
    marginTop: '6px',
    fontSize: '11.5px',
    fontStyle: 'italic',
    color: '#064e3b',
    lineHeight: '1.3'
  },
  recordEvalBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: '1px solid #f59e0b',
    backgroundColor: '#fffbeb',
    color: '#b45309',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  scheduleNextBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: 'none',
    backgroundColor: '#059669',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  hireBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: 'none',
    backgroundColor: '#7c3aed',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  hiredNameBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '6px',
    backgroundColor: '#f5f3ff',
    color: '#6d28d9',
    border: '1px solid #ddd6fe',
    fontSize: '11px',
    fontWeight: '700'
  },
  hiredActionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%'
  },
  hiredActionBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    backgroundColor: '#f5f3ff',
    color: '#6d28d9',
    border: '1px solid #ddd6fe',
    fontSize: '12px',
    fontWeight: '700'
  },
  viewPipelineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '7px',
    border: 'none',
    backgroundColor: '#6d28d9',
    color: '#ffffff',
    fontSize: '12.5px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 24px',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#f8fafc'
  },
  modalHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  modalHeaderIcon: {
    fontSize: '22px'
  },
  modalTitle: {
    margin: 0,
    fontSize: '17px',
    fontWeight: '700',
    color: '#0f172a'
  },
  modalSubtitle: {
    margin: '3px 0 0 0',
    fontSize: '12.5px',
    color: '#64748b'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#94a3b8',
    cursor: 'pointer'
  },
  evalForm: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  evalRatingsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  evalField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  evalLabel: {
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#334155'
  },
  evalSelect: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    color: '#0f172a',
    outline: 'none'
  },
  evalTextarea: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.4',
    resize: 'vertical'
  },
  evalModalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '8px'
  },
  evalCancelBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  evalSubmitBtn: {
    padding: '8px 18px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default BookedCandidatesList;
